import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import FlameHeart from "../components/FlameHeart";
import PdfPreviewModal from "../components/PdfPreviewModal";
import { useAppVersion } from "../hooks/useAppVersion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MESI_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const I = {
  Back: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Download: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  ),
  Eye: (p) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
};

export default function GestioneConsulente({ goHome }) {
  const version = useAppVersion();

  return (
    <div className="min-h-screen bg-[var(--c-bg)] flex flex-col" data-testid="consulente-module">
      <header className="px-8 py-4 flex items-center justify-between bg-[#0a0a0a] text-white border-b border-[#0a0a0a]">
        <div className="flex items-center gap-6">
          <button
            onClick={goHome}
            className="font-mono text-[10px] uppercase tracking-widest text-white/60 hover:text-[var(--c-accent)] flex items-center gap-1.5"
            data-testid="consulente-go-home"
          >
            <I.Back className="w-3.5 h-3.5" /> Menu principale
          </button>
          <div className="flex items-center gap-3">
            <FlameHeart size={28} />
            <div className="font-display text-xl text-white">
              FUOCOIN <span className="text-[var(--c-accent)]">HUB</span>{" "}
              <span className="text-white">Documenti</span>
              <span className="font-mono text-xs text-white/50 ml-3" data-testid="app-version-consulente">v{version}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto">
        <DocumentiView />
      </div>
    </div>
  );
}

function DocumentiView() {
  const [docs, setDocs] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);
  const [filterKind, setFilterKind] = useState("all"); // all|rimborso|presenze
  const [search, setSearch] = useState("");
  const [pdfPreview, setPdfPreview] = useState(null); // { url, filename, title }
  const [pdfLoading, setPdfLoading] = useState(null); // key currently loading

  const load = useCallback(async () => {
    try {
      const d = await axios.get(`${API}/consulente/documenti`);
      setDocs(d.data || []);
    } catch (e) {
      toast.error("Caricamento documenti fallito.");
      setDocs([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!docs) return [];
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (filterKind !== "all" && d.kind !== filterKind) return false;
      if (!q) return true;
      return (
        (d.label || "").toLowerCase().includes(q) ||
        (d.detail || "").toLowerCase().includes(q)
      );
    });
  }, [docs, filterKind, search]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAllVisible = () => {
    // toggle: if all visible are already selected, deselect them; otherwise select all
    const allSelected = filtered.length > 0 && filtered.every((d) => selected.has(d.key));
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((d) => next.delete(d.key));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((d) => next.add(d.key));
        return next;
      });
    }
  };

  const clearSelection = () => setSelected(new Set());

  const openPreview = async (d) => {
    if (pdfLoading) return;
    setPdfLoading(d.key);
    try {
      let url, filename;
      if (d.kind === "rimborso") {
        const r = await axios.get(`${API}/rimborsi/schede/${d.scheda_id}/pdf`, { responseType: "blob" });
        filename = `${(d.label || "nota-spese").replace(/[^\w.-]+/g, "_")}.pdf`;
        url = window.URL.createObjectURL(r.data);
      } else if (d.kind === "presenze") {
        const r = await axios.get(`${API}/presenze/pdf/${d.tipo}/${d.anno}/${d.mese}`, { responseType: "blob" });
        filename = `${d.tipo}-${d.anno}-${String(d.mese).padStart(2, "0")}.pdf`;
        url = window.URL.createObjectURL(r.data);
      } else {
        toast.error("Anteprima non disponibile.");
        return;
      }
      setPdfPreview({ url, filename, title: d.label });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore anteprima PDF.");
    } finally {
      setPdfLoading(null);
    }
  };

  const closePreview = () => {
    if (pdfPreview?.url) {
      try { window.URL.revokeObjectURL(pdfPreview.url); } catch (_) { /* ignore */ }
    }
    setPdfPreview(null);
  };

  const downloadZip = async () => {
    if (selected.size === 0) {
      toast.error("Seleziona almeno un documento.");
      return;
    }
    setDownloading(true);
    try {
      const r = await axios.post(
        `${API}/consulente/zip`,
        { keys: Array.from(selected) },
        { responseType: "blob" }
      );
      // Trigger download
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `documenti_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(`ZIP scaricato (${selected.size} documenti).`);
    } catch (e) {
      toast.error("Download ZIP fallito.");
    } finally {
      setDownloading(false);
    }
  };

  const removeDoc = async (d) => {
    let warning;
    if (d.kind === "rimborso") {
      warning = `Eliminare definitivamente "${d.label}"? Tutte le righe associate saranno cancellate.`;
    } else if (d.kind === "presenze") {
      const tipoTxt =
        d.tipo === "presenze" ? "presenze (ore lavorate HH)" :
        d.tipo === "assenze" ? "assenze (HHA + motivi)" :
        d.tipo === "straordinarie" ? "straordinarie (HHS)" : d.tipo;
      warning = `Eliminare definitivamente i dati di ${tipoTxt} di "${d.label}"?\n\nQuesta operazione azzera i valori corrispondenti per TUTTI i dipendenti del mese. Irreversibile.`;
    } else {
      toast.error("Elemento non eliminabile.");
      return;
    }
    if (!window.confirm(warning)) return;
    try {
      if (d.kind === "rimborso") {
        await axios.delete(`${API}/rimborsi/schede/${d.scheda_id}`);
      } else {
        await axios.delete(`${API}/consulente/documenti/${encodeURIComponent(d.key)}`);
      }
      toast.success("Documento eliminato.");
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(d.key);
        return next;
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione fallita.");
    }
  };

  if (docs === null) {
    return (
      <div className="p-8 font-mono text-xs text-[var(--c-text-muted)]" data-testid="consulente-loading">
        Caricamento documenti…
      </div>
    );
  }

  const rimborsiCount = docs.filter((d) => d.kind === "rimborso").length;
  const presenzeCount = docs.filter((d) => d.kind === "presenze").length;
  const selCount = selected.size;

  return (
    <div className="p-8" data-testid="view-consulente-documenti">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--c-text-muted)] mb-1">
          ▌ Operatività
        </div>
        <h2 className="font-display text-4xl">Documenti</h2>
        <p className="font-mono text-xs text-[var(--c-text-muted)] mt-1">
          Seleziona uno o più PDF e scaricali in un unico file ZIP, da allegare alla tua email ordinaria verso il consulente.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <SummaryTile label="Note Spese" value={rimborsiCount} testid="tile-rimborsi" />
        <SummaryTile label="Report Presenze" value={presenzeCount} testid="tile-presenze" />
        <SummaryTile label="Selezionati" value={selCount} highlight testid="tile-selezionati" />
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--c-text-muted)] mb-1">Filtra per</div>
          <div className="flex border border-[var(--c-border-soft)]">
            {[
              { id: "all", label: "Tutto" },
              { id: "rimborso", label: "Note Spese" },
              { id: "presenze", label: "Presenze" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilterKind(f.id)}
                className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${
                  filterKind === f.id ? "bg-[var(--c-text)] text-white" : "bg-white text-[var(--c-text)] hover:bg-[var(--c-surface)]"
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--c-text-muted)] mb-1">Cerca</div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, descrizione…"
            className="w-full px-3 py-1.5 border border-[var(--c-border-soft)] bg-white font-mono text-xs"
            data-testid="consulente-search"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={selectAllVisible}
            className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-surface)]"
            data-testid="consulente-select-all"
          >
            {filtered.length > 0 && filtered.every((d) => selected.has(d.key))
              ? "Deseleziona tutto"
              : "Seleziona tutto"}
          </button>
          {selCount > 0 && (
            <button
              onClick={clearSelection}
              className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-border-soft)] text-[var(--c-text-muted)] hover:border-[var(--c-text)]"
              data-testid="consulente-clear"
            >
              Pulisci ({selCount})
            </button>
          )}
          <button
            onClick={downloadZip}
            disabled={downloading || selCount === 0}
            className="px-5 py-1.5 font-mono text-[10px] uppercase tracking-widest bg-[var(--c-accent)] text-white hover:opacity-90 disabled:opacity-40 flex items-center gap-2"
            data-testid="consulente-download-zip"
          >
            <I.Download className="w-3.5 h-3.5" />
            {downloading ? "Preparazione…" : `Scarica ZIP (${selCount})`}
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--c-border-soft)] p-12 text-center font-mono text-sm text-[var(--c-text-muted)]" data-testid="consulente-empty">
          Nessun documento disponibile. Crea Note Spese in <strong>Spese di Viaggio</strong> oppure registra Presenze.
        </div>
      ) : (
        <div className="border border-[var(--c-border-soft)] bg-white">
          <table className="w-full font-mono text-sm">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold w-10"></th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold w-32">Tipo</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Documento</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold">Dettaglio</th>
                <th className="text-left px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold w-32">Periodo</th>
                <th className="text-right px-3 py-2 border-b border-[var(--c-text)] bg-[var(--c-surface)] text-[10px] uppercase tracking-widest font-bold w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const checked = selected.has(d.key);
                const period = (d.anno && d.mese)
                  ? `${MESI_IT[d.mese - 1]} ${d.anno}`
                  : "—";
                return (
                  <tr
                    key={d.key}
                    onClick={() => toggle(d.key)}
                    className={`cursor-pointer ${checked ? "bg-[var(--c-accent)]/5" : "hover:bg-[var(--c-surface)]"}`}
                    data-testid={`consulente-row-${d.key}`}
                  >
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(d.key)}
                        onClick={(e) => e.stopPropagation()}
                        className="accent-[var(--c-accent)] w-4 h-4"
                        data-testid={`consulente-cb-${d.key}`}
                      />
                    </td>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)]">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${
                        d.kind === "rimborso"
                          ? "bg-[var(--c-accent)]/10 text-[var(--c-accent)] border-[var(--c-accent)]/40"
                          : "bg-[var(--c-surface)] text-[var(--c-text)] border-[var(--c-border-soft)]"
                      }`}>
                        {d.kind === "rimborso" ? "NOTA SPESE" : (d.tipo || "PRESENZE").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)] font-semibold">{d.label}</td>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-[var(--c-text-muted)]">{d.detail}</td>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-[var(--c-text-muted)]">{period}</td>
                    <td className="px-3 py-2 border-b border-[var(--c-border-soft)] text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => openPreview(d)}
                          disabled={pdfLoading === d.key}
                          className="px-2 py-1 font-mono text-[10px] uppercase tracking-widest border border-[var(--c-text)] hover:bg-[var(--c-text)] hover:text-white transition-colors disabled:opacity-40 inline-flex items-center gap-1"
                          data-testid={`consulente-preview-${d.key}`}
                          title="Anteprima PDF"
                        >
                          <I.Eye className="w-3 h-3" />
                          {pdfLoading === d.key ? "…" : "PDF"}
                        </button>
                        <button
                          onClick={() => removeDoc(d)}
                          className="px-2 py-1 font-mono text-[10px] uppercase tracking-widest border border-red-300 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                          data-testid={`consulente-del-${d.key}`}
                          title={d.kind === "rimborso" ? "Elimina Nota Spese" : `Elimina dati ${d.tipo || "presenze"} del mese`}
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pdfPreview && (
        <PdfPreviewModal
          url={pdfPreview.url}
          filename={pdfPreview.filename}
          title={pdfPreview.title}
          onClose={closePreview}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, highlight, testid }) {
  return (
    <div
      className={`px-4 py-3 border ${
        highlight && value > 0
          ? "border-[var(--c-accent)] bg-[var(--c-accent)]/5"
          : "border-[var(--c-border-soft)] bg-white"
      }`}
      data-testid={testid}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--c-text-muted)]">{label}</div>
      <div className={`font-display text-3xl tabular-nums ${highlight && value > 0 ? "text-[var(--c-accent)]" : "text-[var(--c-text)]"}`}>
        {value}
      </div>
    </div>
  );
}
